/**
 * Centralized unit type classification and categorization.
 *
 * This module provides:
 * - Categorized lists of unit types (crushers, screens, feeders, etc.)
 * - Helper functions for type checking (supportsRiskAssessment, etc.)
 * - Human-readable type name mappings
 *
 * Use this module to avoid duplication of unit type categorization logic
 * across the codebase.
 */

import { UnitTypes } from 'unitData/types';

// ============================================================================
// Core Unit Type Categories
// ============================================================================

/**
 * All crusher unit types (non-generic).
 */
export const CRUSHER_TYPES = [
  UnitTypes.JAW_CRUSHER_CJ,
  UnitTypes.CONE_CRUSHER_CS,
  UnitTypes.CONE_CRUSHER_CH,
  UnitTypes.IMPACT_CRUSHER_CV,
  UnitTypes.IMPACT_CRUSHER_CI,
  UnitTypes.HYBRID_CRUSHER_CR,
  UnitTypes.GYRATORY_CRUSHER_CG,
] as const;

/**
 * All screen unit types (non-generic).
 */
export const SCREEN_TYPES = [
  UnitTypes.CONVENTIONAL_SCREEN,
  UnitTypes.LINEAR_MOTION_SCREEN,
  UnitTypes.MULTISLOPE_SCREEN,
  UnitTypes.BANANA_SCREEN,
  UnitTypes.SCHENCK_SCREEN,
  UnitTypes.KWATANI_SCREEN,
  UnitTypes.FREEFALL_SCREEN_SF,
  UnitTypes.FREEFALL_SCREEN_SS,
  UnitTypes.GRIZZLY_SCREEN,
] as const;

/**
 * All feeder unit types.
 */
export const FEEDER_TYPES = [
  UnitTypes.PAN_FEEDER_SP,
  UnitTypes.PLATE_FEEDER_SH,
  UnitTypes.GRIZZLY_FEEDER_GENERAL,
  UnitTypes.GRIZZLY_FEEDER_SW,
  UnitTypes.GRIZZLY_FEEDER_SV,
  UnitTypes.GRIZZLY_FEEDER_SV_E,
  UnitTypes.FEEDER_AND_SCREEN,
] as const;

/**
 * Generic unit types (crusher and screen).
 */
export const GENERIC_TYPES = [
  UnitTypes.GENERIC_CRUSHER,
  UnitTypes.GENERIC_SCREEN,
] as const;

/**
 * Mobile tracked unit types.
 */
export const MOBILE_TRACKED_TYPES = [
  UnitTypes.MOBILE_TRACKED_JAW,
  UnitTypes.MOBILE_TRACKED_CONE,
  UnitTypes.MOBILE_TRACKED_HSI,
  UnitTypes.MOBILE_TRACKED_SCREEN,
] as const;

/**
 * Mobile wheeled unit types.
 */
export const MOBILE_WHEELED_TYPES = [
  UnitTypes.MOBILE_WHEELED_JAW,
  UnitTypes.MOBILE_JAW_AND_CONE,
  UnitTypes.MOBILE_WHEELED_CONE,
  UnitTypes.MOBILE_WHEELED_HSI,
  UnitTypes.MOBILE_VSI,
  UnitTypes.MOBILE_WHEELED_SCREEN,
] as const;

/**
 * Storage unit types.
 */
export const STORAGE_TYPES = [
  UnitTypes.STORAGE,
  UnitTypes.STORAGE_FEEDER,
  UnitTypes.STORAGE_FEEDER_2_OUTPUTS,
] as const;

/**
 * Feed unit types.
 */
export const FEED_TYPES = [UnitTypes.FEED] as const;

// ============================================================================
// Composite Categories
// ============================================================================

/**
 * All mobile unit types (tracked and wheeled).
 */
export const MOBILE_TYPES = [
  ...MOBILE_TRACKED_TYPES,
  ...MOBILE_WHEELED_TYPES,
] as const;

/**
 * Unit types that support risk assessment features.
 * Includes all non-generic crushers and screens only.
 * Excludes: generic units, feeders, and other unit types.
 */
export const RISK_ASSESSMENT_TYPES = [
  ...CRUSHER_TYPES,
  ...SCREEN_TYPES,
] as const;

// ============================================================================
// Type Checking Helper Functions
// ============================================================================

/**
 * Checks if a unit type supports risk assessment features.
 *
 * @param type - The unit type to check
 * @returns True if the unit type supports risk assessment
 */
export const supportsRiskAssessment = (type: UnitTypes): boolean => {
  return (RISK_ASSESSMENT_TYPES as readonly UnitTypes[]).includes(type);
};

/**
 * Checks if a unit type is a crusher.
 *
 * @param type - The unit type to check
 * @returns True if the unit type is a crusher
 */
export const isCrusher = (type: UnitTypes): boolean => {
  return (CRUSHER_TYPES as readonly UnitTypes[]).includes(type);
};

/**
 * Checks if a unit type is a screen.
 *
 * @param type - The unit type to check
 * @returns True if the unit type is a screen
 */
export const isScreen = (type: UnitTypes): boolean => {
  return (SCREEN_TYPES as readonly UnitTypes[]).includes(type);
};

/**
 * Checks if a unit type is a feeder.
 *
 * @param type - The unit type to check
 * @returns True if the unit type is a feeder
 */
export const isFeeder = (type: UnitTypes): boolean => {
  return (FEEDER_TYPES as readonly UnitTypes[]).includes(type);
};

/**
 * Checks if a unit type is a mobile unit.
 *
 * @param type - The unit type to check
 * @returns True if the unit type is a mobile unit
 */
export const isMobile = (type: UnitTypes): boolean => {
  return (MOBILE_TYPES as readonly UnitTypes[]).includes(type);
};

// ============================================================================
// Human-Readable Type Names
// ============================================================================

/**
 * Mapping of unit types to human-readable names.
 * Used for display purposes in UI components.
 */
export const UNIT_TYPE_NAMES: Partial<Record<UnitTypes, string>> = {
  // Crushers
  [UnitTypes.CONE_CRUSHER_CH]: 'Cone Crusher',
  [UnitTypes.CONE_CRUSHER_CS]: 'Cone Crusher',
  [UnitTypes.JAW_CRUSHER_CJ]: 'Jaw Crusher',
  [UnitTypes.GYRATORY_CRUSHER_CG]: 'Gyratory Crusher',
  [UnitTypes.IMPACT_CRUSHER_CI]: 'Impact Crusher',
  [UnitTypes.IMPACT_CRUSHER_CV]: 'Impact Crusher',
  [UnitTypes.HYBRID_CRUSHER_CR]: 'Hybrid Crusher',
  [UnitTypes.GENERIC_CRUSHER]: 'Generic Crusher',

  // Screens
  [UnitTypes.CONVENTIONAL_SCREEN]: 'Conventional Screen',
  [UnitTypes.LINEAR_MOTION_SCREEN]: 'Linear Motion Screen',
  [UnitTypes.MULTISLOPE_SCREEN]: 'Multislope Screen',
  [UnitTypes.BANANA_SCREEN]: 'Banana Screen',
  [UnitTypes.SCHENCK_SCREEN]: 'Schenck Screen',
  [UnitTypes.KWATANI_SCREEN]: 'Kwatani Screen',
  [UnitTypes.FREEFALL_SCREEN_SF]: 'Free Fall Screen',
  [UnitTypes.FREEFALL_SCREEN_SS]: 'Free Fall Screen',
  [UnitTypes.GRIZZLY_SCREEN]: 'Grizzly Screen',
  [UnitTypes.GENERIC_SCREEN]: 'Generic Screen',

  // Feeders
  [UnitTypes.PAN_FEEDER_SP]: 'Pan Feeder',
  [UnitTypes.PLATE_FEEDER_SH]: 'Plate Feeder',
  [UnitTypes.GRIZZLY_FEEDER_GENERAL]: 'Grizzly Feeder',
  [UnitTypes.GRIZZLY_FEEDER_SW]: 'Grizzly Feeder',
  [UnitTypes.GRIZZLY_FEEDER_SV]: 'Grizzly Feeder',
  [UnitTypes.GRIZZLY_FEEDER_SV_E]: 'Grizzly Feeder',
  [UnitTypes.FEEDER_AND_SCREEN]: 'Feeder and Screen',

  // Mobile Units
  [UnitTypes.MOBILE_TRACKED_JAW]: 'Mobile Jaw Crusher',
  [UnitTypes.MOBILE_TRACKED_CONE]: 'Mobile Cone Crusher',
  [UnitTypes.MOBILE_TRACKED_HSI]: 'Mobile Impact Crusher',
  [UnitTypes.MOBILE_TRACKED_SCREEN]: 'Mobile Screen',
  [UnitTypes.MOBILE_WHEELED_JAW]: 'Mobile Jaw Crusher',
  [UnitTypes.MOBILE_JAW_AND_CONE]: 'Mobile Jaw and Cone',
  [UnitTypes.MOBILE_WHEELED_CONE]: 'Mobile Cone Crusher',
  [UnitTypes.MOBILE_WHEELED_HSI]: 'Mobile Impact Crusher',
  [UnitTypes.MOBILE_VSI]: 'Mobile VSI Crusher',
  [UnitTypes.MOBILE_WHEELED_SCREEN]: 'Mobile Screen',

  // Storage
  [UnitTypes.STORAGE]: 'Storage',
  [UnitTypes.STORAGE_FEEDER]: 'Storage Feeder',
  [UnitTypes.STORAGE_FEEDER_2_OUTPUTS]: 'Storage Feeder',

  // Feed
  [UnitTypes.FEED]: 'Feed',

  // Other
  [UnitTypes.ADD]: 'Add',
  [UnitTypes.SPLIT]: 'Split',
};

/**
 * Gets the human-readable name for a unit type.
 * Returns the type string itself if no mapping exists.
 *
 * @param type - The unit type
 * @returns Human-readable name for the unit type
 */
export const getUnitTypeName = (type: UnitTypes): string => {
  return UNIT_TYPE_NAMES[type] ?? type;
};
